/**
 * Individual represents an instance (ABox) in the knowledge graph.
 * This is used when creating instances from the toolbox or concept chips.
 *
 * Unlike SchemaClass which represents a concept/class (TBox), Individual represents
 * a specific instance that has a type (rdf:type) relationship to a SchemaClass.
 *
 * The referenced SchemaClass can come from a different OntologySchema (TBox/ABox separation).
 *
 * Implements the Individual interface from semantical-core for unified handling
 * with TBox individuals (from ontology-model).
 */
import Concept from "@semantical/core/modules/vocabulary/Concept";
import SchemaClass from "@semantical/core/modules/ontology-model/SchemaClass";
/**
 * Extract a readable label from a URI (last segment after # or /)
 */
function extractLabelFromUri(uri) {
    const hashIndex = uri.lastIndexOf('#');
    if (hashIndex !== -1)
        return uri.substring(hashIndex + 1);
    const slashIndex = uri.lastIndexOf('/');
    if (slashIndex !== -1)
        return uri.substring(slashIndex + 1);
    return uri;
}
/**
 * Individual represents an instance (ABox) in the knowledge graph.
 * Implements the Individual interface from semantical-core.
 */
export default class Individual {
    constructor(concept, uri, label, modelClass) {
        Object.defineProperty(this, "concept", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The URI of this individual instance */
        Object.defineProperty(this, "individualUri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The display label for this individual */
        Object.defineProperty(this, "individualLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /** The SchemaClass this individual is an instance of (can be from another OntologySchema) */
        Object.defineProperty(this, "modelClass", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Property values for this individual.
         * Key: property URI, Value: array of property values (supports multi-value properties)
         */
        Object.defineProperty(this, "propertyValues", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        this.concept = concept;
        this.individualUri = uri;
        this.individualLabel = label;
        this.modelClass = modelClass;
    }
    /**
     * URIs of classes this individual is an instance of.
     * For ABox individuals, this is derived from the modelClass.
     */
    get classUris() {
        return [this.modelClass.uri];
    }
    /**
     * Check if this individual is an instance of a given class.
     * @param classUri The URI of the class to check
     */
    isInstanceOf(classUri) {
        return this.modelClass.uri === classUri;
    }
    /**
     * Set a property value for this individual (replaces all existing values with single value).
     * @param propertyUri The URI of the data property
     * @param value The value to set, or null to delete all values
     */
    setPropertyValue(propertyUri, value) {
        if (value === null || value === '') {
            this.propertyValues.delete(propertyUri);
        }
        else {
            this.propertyValues.set(propertyUri, [value]);
        }
    }
    /**
     * Get the first property value for this individual (for single-value properties).
     * @param propertyUri The URI of the data property
     * @returns The first value, or undefined if not set
     */
    getPropertyValue(propertyUri) {
        const values = this.propertyValues.get(propertyUri);
        return values && values.length > 0 ? values[0] : undefined;
    }
    /**
     * Get all property values for this individual (for multi-value properties).
     * @param propertyUri The URI of the data property
     * @returns Array of values, or empty array if not set
     */
    getPropertyValues(propertyUri) {
        return this.propertyValues.get(propertyUri) || [];
    }
    /**
     * Add a property value to this individual (for multi-value properties).
     * @param propertyUri The URI of the data property
     * @param value The value to add
     */
    addPropertyValue(propertyUri, value) {
        if (!value)
            return;
        const existing = this.propertyValues.get(propertyUri) || [];
        if (!existing.includes(value)) {
            this.propertyValues.set(propertyUri, [...existing, value]);
        }
    }
    /**
     * Remove a specific property value from this individual.
     * @param propertyUri The URI of the data property
     * @param value The value to remove
     */
    removePropertyValue(propertyUri, value) {
        const existing = this.propertyValues.get(propertyUri) || [];
        const filtered = existing.filter(v => v !== value);
        if (filtered.length === 0) {
            this.propertyValues.delete(propertyUri);
        }
        else {
            this.propertyValues.set(propertyUri, filtered);
        }
    }
    /**
     * Update a property value in place (keeps order).
     * @param propertyUri The URI of the data property
     * @param oldValue The value to replace
     * @param newValue The new value
     */
    updatePropertyValue(propertyUri, oldValue, newValue) {
        const existing = this.propertyValues.get(propertyUri) || [];
        const index = existing.indexOf(oldValue);
        if (index !== -1) {
            existing[index] = newValue;
            this.propertyValues.set(propertyUri, existing);
        }
    }
    /**
     * Delete all property values for a property from this individual.
     * @param propertyUri The URI of the data property
     */
    deletePropertyValue(propertyUri) {
        this.propertyValues.delete(propertyUri);
    }
    /**
     * Check if a property has any values set.
     * @param propertyUri The URI of the data property
     */
    hasPropertyValue(propertyUri) {
        const values = this.propertyValues.get(propertyUri);
        return values !== undefined && values.length > 0;
    }
    /**
     * The display name of this individual.
     * Prefers the individualLabel (from rdfs:label) over the concept's prefLabel.
     */
    get name() {
        return this.individualLabel || this.concept.prefLabel;
    }
    /**
     * The definition of this individual (from the concept)
     */
    get definition() {
        return this.concept.definition;
    }
    /**
     * The identifying URI for this individual
     */
    get uri() {
        return this.individualUri;
    }
    /**
     * The stereotype to display (the class label, e.g., "Belgian Citizen")
     */
    get stereotype() {
        return this.modelClass.label || this.modelClass.name;
    }
    toJSON() {
        // Convert propertyValues Map to a plain object for serialization
        // Store as arrays to support multi-value properties
        const propertyValuesObj = {};
        this.propertyValues.forEach((values, key) => {
            propertyValuesObj[key] = values;
        });
        return {
            type: 'individual',
            concept: {
                href: this.concept.uri,
                prefLabel: this.concept.prefLabel,
                definition: this.concept.definition
            },
            // Individual properties (flattened, no longer nested in owlIndividual)
            individualUri: this.individualUri,
            individualLabel: this.individualLabel,
            // Reference the SchemaClass by URI - resolved via modelClassResolver
            modelClassRef: this.modelClass.uri,
            propertyValues: propertyValuesObj
        };
    }
    /**
     * Deserialize from JSON. Requires a modelClassResolver function to look up the SchemaClass
     * from a TBox OntologySchema by URI.
     */
    static fromJSON(json, vocabulary, modelClassResolver) {
        // Try to get concept from vocabulary, or create from stored data if not found
        let concept;
        if (vocabulary.hasConcept(json.concept.href)) {
            concept = vocabulary.getConcept(json.concept.href);
        }
        else {
            concept = new Concept(json.concept.href, json.concept.prefLabel || json.concept.href, [], [], json.concept.definition || "");
        }
        // Resolve the SchemaClass from the TBox
        const classUri = json.modelClassRef;
        let modelClass;
        if (modelClassResolver && classUri) {
            const resolved = modelClassResolver(classUri);
            if (resolved) {
                modelClass = resolved;
            }
            else {
                // Fallback: create minimal SchemaClass from stored data
                console.warn(`Individual.fromJSON: Could not resolve SchemaClass ${classUri}, using fallback`);
                const classLabel = extractLabelFromUri(classUri);
                const fallbackConcept = new Concept(classUri, classLabel, [], [], "");
                modelClass = new SchemaClass('class', fallbackConcept, { label: classLabel });
            }
        }
        else {
            // No resolver or no classUri - create minimal placeholder
            console.warn(`Individual.fromJSON: No SchemaClass reference for individual ${json.individualUri}`);
            const fallbackConcept = new Concept('unknown', 'Unknown', [], [], "");
            modelClass = new SchemaClass('class', fallbackConcept, { label: 'Unknown' });
        }
        const individual = new Individual(concept, json.individualUri, json.individualLabel, modelClass);
        // Restore property values from JSON
        // Supports both old format (string) and new format (string[])
        if (json.propertyValues) {
            for (const [uri, value] of Object.entries(json.propertyValues)) {
                if (Array.isArray(value)) {
                    // New format: array of strings
                    individual.propertyValues.set(uri, value);
                }
                else if (typeof value === 'string') {
                    // Old format: single string - convert to array
                    individual.propertyValues.set(uri, [value]);
                }
            }
        }
        return individual;
    }
}
